// Canonical option lists for admin dropdowns — single source of truth shared
// between `backend/scripts/seed-defaults.ts` and the admin Features /
// Image Effects pages, so the dropdowns can never drift from what the rest
// of the app actually recognizes.

// `Feature.featureUniqueLabel` values the app currently looks for — one row
// per sidebar tool (see `SIDEBAR_TOOL_FEATURE_KEYS` in `layout.ts`, the
// reverse of this list's `value`s). A feature row with any other key is
// inert — nothing reads it — so the admin "Key" field is restricted to this
// list rather than free text. Order here is the default `position` new rows
// get when seeded, matching `ALL_SIDEBAR_TOOLS`.
export const KNOWN_FEATURE_KEYS: { value: string; label: string; group: string }[] = [
  { value: 'text_tool',       label: 'Text Tool',       group: 'Text' },
  { value: 'image_upload',    label: 'Image Upload',    group: 'Upload' },
  { value: 'clipart_browse',  label: 'Clipart Browse',  group: 'Clipart' },
  { value: 'artwork_browse',  label: 'Artwork Browse',  group: 'Artwork' },
  { value: 'shapes_browse',   label: 'Shapes',          group: 'Shapes' },
  { value: 'bg_color',        label: 'Background',     group: 'Background' },
  { value: 'image_effects',   label: 'Filters',         group: 'Effects' },
  { value: 'vdp_namenumber',  label: 'VDP Name Number', group: 'VDP' },
  { value: 'template_load',   label: 'Template Load',  group: 'Template' },
  { value: 'my_designs',      label: 'My Designs',      group: 'MyDesigns' },
  { value: 'layers_panel',    label: 'Layers',          group: 'Layers' },
  { value: 'qr_code',         label: 'QR Code',         group: 'Toolbar' },
  // Read directly by the preview panels (Model3DPreview/PreviewView) by key —
  // NOT a sidebar tool, so it has no entry in `SIDEBAR_TOOL_FEATURE_KEYS`.
  { value: 'object_variants', label: 'Object Variants', group: 'ObjectVariants' },
];

export const KNOWN_FEATURE_GROUPS: string[] = [
  'Text', 'Upload', 'Clipart', 'Artwork', 'Shapes', 'Background', 'Effects',
  'VDP', 'Template', 'MyDesigns', 'Layers', 'Toolbar', 'ObjectVariants', 'Others',
];

// `ImageEffect.value` — the filter-type identifier `ImageEffectsTool.tsx`'s
// `buildFilter()` switches on. Anything outside this list silently does
// nothing on the canvas, so the admin "Type" field is restricted to it.
export const IMAGE_EFFECT_TYPES: { value: string; label: string; defaultValue?: string }[] = [
  { value: 'grayscale',   label: 'Grayscale' },
  { value: 'blackwhite',  label: 'Black & White' },
  { value: 'sepia',       label: 'Sepia' },
  { value: 'invert',      label: 'Invert' },
  { value: 'blur',        label: 'Blur',        defaultValue: '0.05' },
  { value: 'brightness',  label: 'Brightness',  defaultValue: '0.1' },
  { value: 'contrast',    label: 'Contrast',    defaultValue: '0.25' },
  { value: 'saturation',  label: 'Saturation',  defaultValue: '0.4' },
  { value: 'pixelate',    label: 'Pixelate',    defaultValue: '8' },
  { value: 'noise',       label: 'Noise',       defaultValue: '200' },
  { value: 'hue',         label: 'Hue Rotation', defaultValue: '0.5' },
  { value: 'sharpen',     label: 'Sharpen' },
  { value: 'emboss',      label: 'Emboss' },
  { value: 'brownie',     label: 'Brownie' },
  { value: 'vintage',     label: 'Vintage' },
  { value: 'kodachrome',  label: 'Kodachrome' },
  { value: 'technicolor', label: 'Technicolor' },
  { value: 'polaroid',    label: 'Polaroid' },
];

// Distinct known-good `defaultValue` presets, labeled with which Type(s) they
// belong to — derived from `IMAGE_EFFECT_TYPES` so it can't drift out of sync.
export const IMAGE_EFFECT_VALUE_PRESETS: { value: string; label: string }[] = [
  { value: '', label: '(none)' },
  ...Array.from(new Set(IMAGE_EFFECT_TYPES.map(e => e.defaultValue).filter((v): v is string => !!v)))
    .map(value => ({
      value,
      label: `${value} — ${IMAGE_EFFECT_TYPES.filter(e => e.defaultValue === value).map(e => e.label).join(', ')}`,
    })),
];
